import { FC } from 'react';
import { CubeTransparentIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { FRAMEWORK_BY_ID, confidenceLevel, type FrameworkId } from '@/constants/frameworks.ts';

type Props = {
  frameworkId: string | null | undefined;
  confidence: number | null | undefined;
  isLoading?: boolean;
};

const CommandSignalChip: FC<Props> = ({ frameworkId, confidence, isLoading }) => {
  if (!isLoading && !frameworkId) return null;

  const meta = frameworkId ? FRAMEWORK_BY_ID[frameworkId as FrameworkId] : null;
  const level = confidence != null ? confidenceLevel(confidence) : null;

  return (
    <div className="box-border flex flex-col items-start p-4 gap-2.5 border border-[rgba(204,204,204,0.5)] rounded-xl w-full">
      <div className="flex flex-col items-start gap-2 w-full">
        {/* Header row */}
        <div className="flex flex-row items-center gap-2 opacity-87">
          <CubeTransparentIcon className="w-[18px] h-[18px] text-black flex-shrink-0" />
          <span className="font-newsreader font-extrabold text-xl leading-none text-[#0F1B2A]">
            COMMAND Signal
          </span>
        </div>

        {/* Classification line */}
        <p className="font-dm font-normal text-xs leading-[150%] tracking-[0.15px] text-black/60 w-full flex items-center gap-1.5 flex-wrap">
          {isLoading || !meta ? (
            <span className="inline-block h-3 w-56 bg-black/10 rounded animate-pulse" />
          ) : (
            <>
              Current classification:{' '}
              <strong className="font-semibold text-black/80">
                {meta.domainName} ({meta.label})
              </strong>
              <span
                className="tooltip tooltip-left cursor-default flex-shrink-0 before:max-w-[220px] before:text-left before:text-xs before:font-dm"
                data-tip={meta.description}
              >
                <InformationCircleIcon className="w-3.5 h-3.5 text-black/60" />
              </span>
            </>
          )}
        </p>

        {/* Confidence line */}
        <p className="font-dm font-normal text-xs leading-[150%] tracking-[0.15px] text-black/60">
          {isLoading || !level ? (
            <span className="inline-block h-3 w-24 bg-black/10 rounded animate-pulse" />
          ) : (
            <>
              Confidence:{' '}
              <strong className={confidenceColorClass(level)}>{level}</strong>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default CommandSignalChip;

function confidenceColorClass(level: 'High' | 'Moderate' | 'Low'): string {
  if (level === 'High') return 'font-semibold text-[#2E7D32]';
  if (level === 'Moderate') return 'font-semibold text-[#E65100]';
  return 'font-semibold text-[#C62828]';
}
